import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { api, type AreaInput, type AreaUserInput } from '../../api/endpoints';
import type { Area, Responsibility } from '../../api/types';
import { useMe } from '../../auth/context';
import { ErrorMessage } from '../../components/ErrorMessage';
import { PageLoader } from '../../components/Loader';
import { MutationError } from '../../components/MutationError';
import { SettingRow, SettingsRows } from '../../components/Settings';
import { areaStatusLabels, elementTypeLabels, responsibilityLabels, ruleText } from '../../geo/labels';
import { useProject } from '../../projects/context';
import { projectKeys, useProjectApi } from '../../projects/useProjectApi';
import { AreaMapEditor } from './AreaMapEditor';
import { ElementPicker, type PickedElement } from './ElementPicker';
import { AreaOpenItems } from '../../ops/OpenItems';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

// Rollen, die für eine Zuständigkeit infrage kommen (Rolle = Dürfen, Gebiet = Zuständigkeit).
const responsibilityRoles: Record<Responsibility, string[]> = {
  FIELD_SALES: ['FIELD_SALES'],
  TELESALES: ['TELESALES'],
  SUPPORT: ['SUPPORT'],
  SUPERVISION: ['SUPERVISOR', 'PROJECT_LEAD'],
};

export function AreaDetailPage() {
  const { areaId = '' } = useParams();
  const { project, can } = useProject();
  const me = useMe();
  const queryClient = useQueryClient();
  const area = useQuery({
    queryKey: [...projectKeys.areas(project.id), 'detail', areaId],
    queryFn: ({ signal }) => api.area(areaId, project.id, signal),
  });

  if (area.isPending) return <PageLoader label="Gebiet wird geladen …" />;
  if (area.isError) return <ErrorMessage error={area.error} onRetry={() => void area.refetch()} />;

  const a = area.data;
  const owned = a.project_id === project.id;
  const global = (p: 'area.edit' | 'area.delete') => me.global_permissions.includes(p);
  const editable = a.status !== 'ARCHIVED' && (owned ? can('area.edit') : global('area.edit'));
  const deletable = a.status !== 'ARCHIVED' && (owned ? can('area.delete') : global('area.delete'));
  const assignable = a.status !== 'ARCHIVED' && can('area.assign_user');
  const refresh = (next?: Area) => {
    if (next) queryClient.setQueryData([...projectKeys.areas(project.id), 'detail', areaId], next);
    void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
  };

  return (
    <div className="page">
      <p>
        <Link to={`/p/${project.key}/gebiete`}>← Alle Gebiete</Link>
      </p>
      <header className="page-header">
        <h1>{a.name}</h1>
        <div className="badges">
          <span className={a.status === 'ACTIVE' ? 'badge badge-ok' : 'badge badge-muted'}>{areaStatusLabels[a.status]}</span>
          {!a.project_id && <span className="badge badge-warn">Projektübergreifend</span>}
          {a.kind === 'RULE' && <span className="badge">Regelgebiet</span>}
        </div>
      </header>
      {a.kind === 'RULE' && <p className="alert alert-info">{ruleText(a)}</p>}
      {!owned && (
        <p className="alert alert-info">
          Dieses Gebiet wird projektübergreifend gepflegt ({a.projects.map((p) => p.name).join(', ')}). Zusammensetzung und Name
          ändern Administratoren; Zuständigkeiten für {project.name} können Sie hier pflegen.
        </p>
      )}
      <AreaOpenItems areaId={a.id} />
      <MasterData area={a} editable={editable} onSaved={refresh} />
      <Composition area={a} editable={editable} onSaved={refresh} />
      <Staff area={a} assignable={assignable} onSaved={() => refresh()} />
      {deletable && <Archive area={a} />}
    </div>
  );
}

function MasterData({ area, editable, onSaved }: { area: Area; editable: boolean; onSaved: (a: Area) => void }) {
  const [form, setForm] = useState<AreaInput>({ name: area.name, description: area.description, status: area.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE' });
  const save = useMutation({ mutationFn: () => api.updateArea(area.id, form), onSuccess: onSaved });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <section className="card" aria-labelledby="area-master">
      <h2 id="area-master">Stammdaten</h2>
      {editable ? (
        <form onSubmit={submit}>
          <SettingsRows>
            <SettingRow label="Name" htmlFor="area-name">
              <input
                id="area-name"
                className="setting-wide"
                value={form.name}
                required
                minLength={2}
                maxLength={100}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </SettingRow>
            <SettingRow label="Beschreibung" hint="Für die Übersicht, z. B. Region oder Besonderheiten" htmlFor="area-description">
              <input
                id="area-description"
                className="setting-wide"
                value={form.description}
                maxLength={1000}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </SettingRow>
            <SettingRow label="Status" hint="Deaktivierte Gebiete ordnen keine neuen Leads zu" htmlFor="area-status">
              <select id="area-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as AreaInput['status'] })}>
                <option value="ACTIVE">Aktiv</option>
                <option value="INACTIVE">Deaktiviert</option>
              </select>
            </SettingRow>
          </SettingsRows>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              Speichern
            </button>
            {save.isSuccess && (
              <span className="muted" role="status">
                Gespeichert.
              </span>
            )}
          </div>
          {save.isError && <MutationError error={save.error} />}
        </form>
      ) : (
        <dl className="facts">
          <dt>Beschreibung</dt>
          <dd>{area.description || '–'}</dd>
          <dt>Projekte</dt>
          <dd>{area.projects.map((p) => p.name).join(', ') || '–'}</dd>
        </dl>
      )}
    </section>
  );
}

function Composition({ area, editable, onSaved }: { area: Area; editable: boolean; onSaved: (a: Area) => void }) {
  const add = useMutation({ mutationFn: (e: PickedElement) => api.addAreaElement(area.id, e), onSuccess: onSaved });
  const remove = useMutation({ mutationFn: (elementId: string) => api.removeAreaElement(area.id, elementId), onSuccess: onSaved });
  return (
    <section className="card" aria-labelledby="area-elements">
      <h2 id="area-elements">Zusammensetzung</h2>
      <p className="muted small">
        Bei Überschneidungen gilt: PLZ vor Gemeinde vor Landkreis vor Bundesland; bei Gleichstand entscheidet die Priorität.
      </p>
      <AreaMapEditor area={area} editable={editable} onSaved={onSaved} />
      {area.elements.length === 0 ? (
        <p className="muted">Noch keine Bestandteile.</p>
      ) : (
        <ul className="element-list">
          {area.elements.map((e) => (
            <li key={e.id}>
              <span className="badge">{elementTypeLabels[e.type]}</span>
              <span>
                <strong>{e.type === 'POSTAL_CODE' ? e.code : e.name}</strong> <span className="muted">{e.detail}</span>
              </span>
              {editable && (
                <button
                  type="button"
                  className="btn btn-small btn-ghost"
                  aria-label={`${e.name} entfernen`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(e.id)}
                >
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && <ElementPicker busy={add.isPending} onPick={(e) => add.mutate(e)} />}
      {add.isError && <MutationError error={add.error} />}
      {remove.isError && <MutationError error={remove.error} />}
    </section>
  );
}

function Staff({ area, assignable, onSaved }: { area: Area; assignable: boolean; onSaved: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const members = useQuery({
    queryKey: projectKeys.members(project.id),
    queryFn: ({ signal }) => papi.members(signal),
    enabled: assignable,
  });
  const [form, setForm] = useState<AreaUserInput>({ user_id: '', responsibility: 'FIELD_SALES', is_primary: false });
  const add = useMutation({
    mutationFn: () =>
      api.addAreaUser(area.id, {
        ...form,
        project_id: project.id,
        valid_from: form.valid_from || undefined,
        valid_until: form.valid_until || undefined,
      }),
    onSuccess: () => {
      setForm({ user_id: '', responsibility: form.responsibility, is_primary: false });
      onSaved();
    },
  });
  const end = useMutation({ mutationFn: (assignmentId: string) => api.endAreaUser(area.id, assignmentId), onSuccess: onSaved });
  const roles = responsibilityRoles[form.responsibility ?? 'FIELD_SALES'];
  const candidates = new Map<string, string>();
  for (const m of members.data?.items ?? []) {
    if (m.status === 'ACTIVE' && roles.includes(m.role_key)) candidates.set(m.user_id, `${m.first_name} ${m.last_name}`);
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  const users = area.users.filter((u) => u.project_id === project.id);

  return (
    <section className="card" aria-labelledby="area-staff">
      <h2 id="area-staff">Zuständige Mitarbeiter</h2>
      {users.length === 0 ? (
        <p className="muted">Für {project.name} ist noch niemand zugeordnet.</p>
      ) : (
        <ul className="element-list">
          {users.map((u) => (
            <li key={u.id}>
              <span className="badge">{responsibilityLabels[u.responsibility]}</span>
              <span>
                <strong>
                  {u.first_name} {u.last_name}
                </strong>{' '}
                {u.is_primary && <span className="badge badge-ok">Hauptzuständig</span>}{' '}
                <span className="muted">
                  {u.valid_from || u.valid_until
                    ? `${u.valid_from ? dateFormat.format(new Date(u.valid_from)) : 'offen'} – ${u.valid_until ? dateFormat.format(new Date(u.valid_until)) : 'offen'}`
                    : 'unbefristet'}
                  {!u.active && ' · derzeit nicht gültig'}
                </span>
              </span>
              {assignable && (
                <button
                  type="button"
                  className="btn btn-small btn-ghost"
                  aria-label={`${u.first_name} ${u.last_name} entfernen`}
                  disabled={end.isPending}
                  onClick={() => end.mutate(u.id)}
                >
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {assignable && (
        <form className="form-grid" onSubmit={submit} aria-label="Mitarbeiter zuordnen">
          <label className="field">
            <span>Zuständigkeit</span>
            <select
              value={form.responsibility}
              onChange={(e) => setForm({ ...form, responsibility: e.target.value as Responsibility, user_id: '' })}
            >
              {Object.entries(responsibilityLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Mitarbeiter</span>
            <select value={form.user_id} required onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
              <option value="">Bitte wählen</option>
              {[...candidates].map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Gültig ab</span>
            <input type="date" value={form.valid_from ?? ''} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
          </label>
          <label className="field">
            <span>Gültig bis</span>
            <input type="date" value={form.valid_until ?? ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={!!form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
            Hauptzuständig
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={add.isPending || !form.user_id}>
              Zuordnen
            </button>
          </div>
          {members.isSuccess && candidates.size === 0 && (
            <p className="muted small">Im Projekt gibt es keine aktiven Mitglieder mit passender Rolle.</p>
          )}
          {add.isError && <MutationError error={add.error} />}
        </form>
      )}
      {end.isError && <MutationError error={end.error} />}
    </section>
  );
}

function Archive({ area }: { area: Area }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const archive = useMutation({
    mutationFn: () => api.archiveArea(area.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
      navigate(`/p/${project.key}/gebiete`);
    },
  });
  return (
    <section className="card danger-zone" aria-labelledby="area-archive">
      <h2 id="area-archive">Archivieren</h2>
      <p className="muted small">Archivierte Gebiete bleiben mit ihrer Historie erhalten, werden aber nicht mehr für Zuordnungen verwendet.</p>
      {confirming ? (
        <div className="form-actions">
          <button type="button" className="btn btn-danger" disabled={archive.isPending} onClick={() => archive.mutate()}>
            Endgültig archivieren
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)}>
            Abbrechen
          </button>
        </div>
      ) : (
        <button type="button" className="btn" onClick={() => setConfirming(true)}>
          Gebiet archivieren
        </button>
      )}
      {archive.isError && <MutationError error={archive.error} />}
    </section>
  );
}
